export {
  BusinessAffiliateRewardModel,
  BusinessReferralStatus,
} from '@afriex/core/domain/enums';

import {
  BusinessAffiliateRewardModel,
  BusinessReferralStatus,
} from '@afriex/core/domain/enums';
import type {
  BusinessAffiliateTier as CoreBusinessAffiliateTier,
  BusinessReferral as CoreBusinessReferral,
  Serialized,
} from '@afriex/core/domain/types';

export type {BusinessReferralTotals} from '@afriex/core/domain/types';

/**
 * The tier as GET/POST/PATCH /v2/admin/business-affiliate/tiers return it.
 * `id` is optional in core (a tier not yet stored has none) but is always on
 * the wire; `createdAt` and `updatedAt` are omitted because
 * ADMIN_BUSINESS_AFFILIATE_TIER_FIELDS never returns them.
 */
export type AdminBusinessAffiliateTier = Omit<
  CoreBusinessAffiliateTier,
  'createdAt' | 'updatedAt'
> & {
  id: string;
};

/**
 * A row of GET /v2/admin/business-affiliate/referrals. The Pick lists exactly
 * the ADMIN_BUSINESS_REFERRAL_LIST_FIELDS columns (the rest of the record, such
 * as activatedAt and refereeDiscountPercent, never leaves the server). Dates
 * are ISO strings over JSON, hence Serialized; `id` and `createdAt` are
 * optional in core but always present on a stored referral.
 */
export type AdminBusinessReferral = Pick<
  Serialized<CoreBusinessReferral>,
  | 'referrerBusinessId'
  | 'refereeBusinessId'
  | 'code'
  | 'tierId'
  | 'status'
  | 'totals'
  | 'selfReferralSignals'
  | 'disqualifiedReason'
  | 'signedUpAt'
  | 'activationDeadlineAt'
  | 'rewardEndsAt'
> & {
  id: string;
  createdAt: string;
};

export type AffiliateReferralListParams = {
  page?: number;
  limit?: number;
  status?: BusinessReferralStatus;
  referrerBusinessId?: string;
  refereeBusinessId?: string;
  isFlagged?: boolean;
};

export type CreateAffiliateTierPayload = {
  name: string;
  rewardModel: BusinessAffiliateRewardModel;
  activationWindowDays: number;
  rewardWindowDays: number;
  termsVersion: string;
  isDefault?: boolean;
  refereeDiscountPercent?: number;
  maxRewardPerReferralUSD?: number;
  maxRewardPerPayoutUSD?: number;
  requiresVerifiedReferrer?: boolean;
  referrerSharePercent?: number;
  volumeThresholdUSD?: number;
  flatRewardUSD?: number;
  volumeRewardBasisPoints?: number;
};

export type UpdateAffiliateTierPayload = Partial<{
  name: string;
  isDeactivated: boolean;
  activationWindowDays: number;
  rewardWindowDays: number;
  refereeDiscountPercent: number;
  maxRewardPerReferralUSD: number;
  maxRewardPerPayoutUSD: number;
  requiresVerifiedReferrer: boolean;
  termsVersion: string;
}>;

export type DisqualifyAffiliateReferralPayload = {
  reason: string;
};

export const affiliateReferralStatusColors: Record<
  BusinessReferralStatus,
  string
> = {
  [BusinessReferralStatus.Attributed]: 'blue',
  [BusinessReferralStatus.Activated]: 'successful',
  [BusinessReferralStatus.Expired]: 'gray',
  [BusinessReferralStatus.Completed]: 'indigo',
  [BusinessReferralStatus.Disqualified]: 'failed',
};

export const affiliateRewardModelFields: Record<
  BusinessAffiliateRewardModel,
  (keyof CreateAffiliateTierPayload)[]
> = {
  [BusinessAffiliateRewardModel.FeeShare]: ['referrerSharePercent'],
  [BusinessAffiliateRewardModel.FlatAtThreshold]: [
    'volumeThresholdUSD',
    'flatRewardUSD',
  ],
  [BusinessAffiliateRewardModel.VolumePercentage]: ['volumeRewardBasisPoints'],
};
